import {
    Base,
    getProviderProps,
    getTranslationKey,
    languageConverter,
    LANGUAGE_COLLECTION_NAME,
    Translatable,
    Translation,
    translationCoverter,
    TRANSLATIONS_COLLECTION_NAME,
    TranslationTarget,
} from 'models';
import { useNavigate, useParams } from 'react-router-dom';
import { collection, doc, setDoc, updateDoc, where, addDoc } from 'firebase/firestore';
import { db, useAuthState } from 'firebaseStore';
import { DocumentProvider, useDocumentContext } from 'components/settings/documentContext';
import { ChangeEvent, KeyboardEvent, useEffect, useRef, useState } from 'react';
import {
    CollectionProvider,
    CollectionState,
    useCollectionContext,
} from 'components/settings/collectionContext';
import { Button } from 'components/button';
import { InputFromList } from 'components/settings/recordList';
import './translationsPage.css';
import { useCommitChanges } from 'commits';

interface TextareaProps {
    value: string;
    setValue: (_: string) => void;
}
const Textarea = ({ value, setValue }: TextareaProps) => {
    const setHeight = (element: HTMLTextAreaElement) => {
        element.style.height = `${element.scrollHeight}px`;
    };
    const inputEl = useRef<HTMLTextAreaElement>(null);
    const setHeight_ = () => (inputEl.current ? setHeight(inputEl.current) : undefined);
    useEffect(() => setHeight_());
    const textAreaAdjust = (
        keyup: KeyboardEvent<HTMLTextAreaElement> | ChangeEvent<HTMLTextAreaElement>
    ) => setHeight(keyup.currentTarget);
    return (
        <textarea
            ref={inputEl}
            onKeyUp={textAreaAdjust}
            className="translationInput"
            value={value}
            onChange={(e) => {
                setValue(e.target.value);
                textAreaAdjust(e);
            }}
            placeholder=""
        />
    );
};

const textStyles = {
    flexBasis: '300px',
    flexGrow: 1,
    borderLeft: '1px solid rgb(216 216 216 / 50%)',
    paddingLeft: '3px',
    marginLeft: '3px',
};
interface TranslationRowProps {
    targetType: Translatable;
    record: TranslationTarget;
    translationMap: Map<string, Translation>;
}
interface TranslationRowItemProps {
    targetType: Translatable;
    record: Base;
    targetField: 'name' | 'content';
    translation?: Translation;
    onSave: (_: string) => Promise<any>;
}
const TranslationRowItem = ({
    record,
    translation,
    targetField,
    onSave,
}: TranslationRowItemProps) => {
    const [value, setValue] = useState('');
    const [editing, edit] = useState(false);
    const showBox = !translation?.content || editing;
    const hasChanged = value !== (translation?.content || '');
    return (
        <div className="translationRowItem">
            <div
                style={{
                    flexBasis: '300px',
                    flexGrow: 1,
                    display: 'flex',
                    alignItems: targetField === 'name' ? 'center' : undefined,
                    whiteSpace: 'break-spaces',
                }}
            >
                {record[targetField]}
            </div>
            {targetField === 'content' || record.__type !== 'Clause' ? (
                <div style={textStyles}>
                    <div style={{ position: 'relative' }}>
                        {showBox ? (
                            <Textarea value={value} setValue={setValue} />
                        ) : (
                            <div
                                style={{ cursor: 'pointer', whiteSpace: 'break-spaces' }}
                                onClick={() => {
                                    edit(true);
                                    setValue(translation?.content || '');
                                }}
                            >
                                {translation?.content}
                            </div>
                        )}
                        {showBox && (translation || hasChanged) ? (
                            <div
                                style={{
                                    position: 'absolute',
                                    width: '100%',
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                }}
                            >
                                <Button
                                    theme="blue"
                                    style={{
                                        width: translation ? '50px' : '37px',
                                        height: '23px',
                                        zIndex: 1,
                                    }}
                                    onClick={() =>
                                        hasChanged
                                            ? onSave(value.trim()).then(() => edit(false))
                                            : edit(false)
                                    }
                                >
                                    {hasChanged ? (translation ? 'Update' : 'Save') : 'Close'}
                                </Button>
                            </div>
                        ) : null}
                    </div>
                </div>
            ) : (
                <div style={textStyles}>--</div>
            )}
        </div>
    );
};

const TranslationRow = ({ targetType, translationMap, record }: TranslationRowProps) => {
    const [language] = useLanguage();
    const [, , tenant] = useAuthState();
    const languageIndexId = language?.indexId();
    const commitChanges = useCommitChanges();

    const nameTranslation = translationMap.get(
        getTranslationKey({ targetField: 'name', targetType, targetId: record.id })
    );
    const contentTranslation = translationMap.get(
        getTranslationKey({ targetField: 'content', targetType, targetId: record.id })
    );
    const getOnSave =
        (targetField: string, translation?: Translation) => async (content: string) => {
            if (translation) {
                await commitChanges({
                    target: record,
                    action: 'update',
                    languageIndexId,
                    message: '',
                    changes: [
                        {
                            field: targetField,
                            before: translation.content,
                            after: content,
                            beforeValues: null,
                            afterValues: null,
                        },
                    ],
                });
                return setDoc(
                    doc(db, TRANSLATIONS_COLLECTION_NAME, translation.id),
                    { content },
                    { merge: true, mergeFields: ['content'] }
                );
            }
            const data = {
                tenantId: tenant,
                content,
                targetId: record.id,
                targetType,
                targetField,
                languageIndexId,
            };
            await commitChanges({
                target: record,
                action: 'add',
                languageIndexId,
                message: '',
                changes: [
                    {
                        field: targetField,
                        before: null,
                        after: content,
                        beforeValues: null,
                        afterValues: null,
                    },
                ],
            });
            return addDoc(collection(db, TRANSLATIONS_COLLECTION_NAME), data).then(
                (newDocRef) => {
                    return updateDoc(
                        newDocRef,
                        new Translation({ ...data, id: newDocRef.id }).toFirestore()
                    ).then(
                        () => Promise.resolve(),
                        (error) => {
                            console.error(
                                `TranslationRow getOnSave updateDoc error ${TRANSLATIONS_COLLECTION_NAME}`,
                                error
                            );
                        }
                    );
                },
                (error) => {
                    console.error(
                        `TranslationRow getOnSave addDoc error ${TRANSLATIONS_COLLECTION_NAME}`,
                        error
                    );
                }
            );
        };
    return (
        <div className="translationRow">
            <TranslationRowItem
                targetType={targetType}
                translation={nameTranslation}
                record={record}
                targetField="name"
                onSave={getOnSave('name', nameTranslation)}
            />
            {record.__type === 'Clause' ? (
                <TranslationRowItem
                    targetType={targetType}
                    translation={contentTranslation}
                    record={record}
                    targetField="content"
                    onSave={getOnSave('content', contentTranslation)}
                />
            ) : null}
        </div>
    );
};

interface TranslationsProps {
    targetType: Translatable;
    tag: string;
}
const Translations = ({ tag, targetType }: TranslationsProps) => {
    const [translations] = useCollectionContext(TRANSLATIONS_COLLECTION_NAME, [
        where('tags', 'array-contains', tag),
    ]) as CollectionState<Translation>;
    const { collectionName } = getProviderProps(targetType);
    const [records, pending] = useCollectionContext(collectionName);
    if (pending) {
        return <div>Loading records</div>;
    }
    if (!records?.length) {
        return <div>No records found</div>;
    }
    return (
        <TranslationsDisplay
            records={records as TranslationTarget[]}
            translations={translations || []}
        />
    );
};
export interface TranslationsDisplayProps {
    records: TranslationTarget[];
    translations: Translation[];
}
export const TranslationsDisplay = ({ records, translations }: TranslationsDisplayProps) => {
    const translationMap: Map<string, Translation> = new Map(
        translations.map((record) => [getTranslationKey({ ...record }), record])
    );
    const countDone = records.filter((record) => {
        const targetType = record.__type as Translatable;
        const key = getTranslationKey({ targetField: 'name', targetType, targetId: record.id });
        const nameTranslation = translationMap.get(key);
        if (!nameTranslation && record.__type !== 'Clause') {
            return false;
        }
        if (!record.content) {
            return true;
        }
        return !!translationMap.get(
            getTranslationKey({ targetField: 'content', targetType, targetId: record.id })
        )?.content;
    }).length;
    const percentage = `${records.length ? Math.round((countDone * 100) / records.length) : 0}%`;
    return (
        <div style={{ width: '100%' }}>
            <div
                style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '20px',
                }}
            >
                <div style={{ width: 'calc(100% - 50px)', position: 'relative' }}>
                    <div
                        style={{
                            width: '100%',
                            backgroundColor: '#D8D8D8',
                            borderRadius: '4px',
                            height: '11px',
                        }}
                    ></div>
                    <div
                        style={{
                            width: percentage,
                            position: 'absolute',
                            backgroundColor: '#18A0FB',
                            borderRadius: '4px',
                            height: '11px',
                            marginTop: '-11px',
                            transition: 'all 0.45s',
                        }}
                    ></div>
                </div>
                <div style={{ width: '50px', display: 'flex', justifyContent: 'flex-end' }}>
                    {percentage}
                </div>
            </div>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                }}
            >
                <div style={{ flexGrow: 1 }}>
                    {records?.map((record, index) => (
                        <TranslationRow
                            key={
                                record.__type === 'Subsection' || record.__type === 'Coverage'
                                    ? `${record.id}-${index}` // can appear multiple times
                                    : record.id
                            }
                            targetType={record.__type as Translatable}
                            record={record}
                            translationMap={translationMap}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

export const useLanguage = () => {
    const { languageId } = useParams();
    return useDocumentContext(LANGUAGE_COLLECTION_NAME, languageId as string);
};

const TranslationsPage = () => {
    const { languageId } = useParams();
    const [targetType, setTarget] = useState<Translatable | ''>('Clause');
    const navigate = useNavigate();
    const [language, pending] = useLanguage();
    const [languages] = useCollectionContext(LANGUAGE_COLLECTION_NAME);
    if (pending) {
        return <div>Loading language {languageId}</div>;
    }
    if (!language) {
        return <div>Language {languageId} not found</div>;
    }
    const tag = `languageIndexId-${targetType}-${language.indexId()}`;
    return (
        <div
            style={{
                padding: '70px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
            }}
        >
            <div style={{ fontSize: '34px' }}>Translations</div>
            <div
                style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '20px',
                }}
            >
                <div style={{ fontSize: '24px', width: '241px' }}>
                    <InputFromList
                        value={{ name: language.name, id: language.indexId() }}
                        onSelect={(inputValue) =>
                            inputValue && navigate(`/translations/${inputValue.id}`)
                        }
                        values={
                            languages
                                ? languages.map((language_) => {
                                      return { name: language_.name, id: language_.id };
                                  })
                                : []
                        }
                    />
                </div>
            </div>
            <div
                style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <div style={{ width: '241px', marginBottom: '20px' }}>
                    <InputFromList
                        value={{ name: targetType, id: targetType }}
                        onSelect={(selected) =>
                            selected ? setTarget(selected.id as Translatable) : undefined
                        }
                        values={[
                            { name: 'Clause', id: 'Clause' },
                            { name: 'Section', id: 'Section' },
                            { name: 'Subsection', id: 'Subsection' },
                            { name: 'Coverage', id: 'Coverage' },
                        ]}
                    />
                </div>
            </div>
            {targetType ? (
                <CollectionProvider {...getProviderProps(targetType)}>
                    <CollectionProvider
                        collectionName={TRANSLATIONS_COLLECTION_NAME}
                        converter={translationCoverter}
                        constraints={[where('tags', 'array-contains', tag)]}
                    >
                        <Translations tag={tag} targetType={targetType} />
                    </CollectionProvider>
                </CollectionProvider>
            ) : (
                <div>select</div>
            )}
        </div>
    );
};

const TranslationsPageContainer = () => {
    const { languageId } = useParams();
    if (!languageId) {
        return <div>Not found</div>;
    }
    return (
        <DocumentProvider
            collectionName={LANGUAGE_COLLECTION_NAME}
            converter={languageConverter}
            id={languageId}
        >
            <CollectionProvider
                collectionName={LANGUAGE_COLLECTION_NAME}
                converter={languageConverter}
            >
                <TranslationsPage />
            </CollectionProvider>
        </DocumentProvider>
    );
};
export { TranslationsPageContainer as TranslationsPage };
